import { useQuery } from '@tanstack/react-query';

import { useApiAuth } from '~/hooks/useApiAuth';
import { useAuth } from '~/hooks/useAuth';
import { endpoints } from '~/config/endpoints';

// Read model en árbol (espejo del backend store-legacy). N niveles vía children;
// `id` es string (el backend ya lo expone así, listo para UUIDs).
export type StoreCategory = {
  id: string;
  name: string;
  icon: string | null;
  image: string | null;
  level: number;
  children: StoreCategory[];
};

// Detecta la categoría "Cupones" (tolerante a acentos/plural/mayúsculas).
const isCupones = (c: StoreCategory) => /cup[oó]n/i.test(c.name);

// Elige hasta `count` categorías AL AZAR (Fisher-Yates) y las devuelve ORDENADAS
// alfabéticamente. "Cupones" es un caso especial: le damos MÍNIMA visibilidad —
// se reserva su lugar y SIEMPRE se renderiza AL FINAL (findable, pero última
// posición), nunca en el subconjunto aleatorio. Memoizalo para no re-barajar.
export function pickRandomCategories(categories: StoreCategory[], count: number): StoreCategory[] {
  const cupones = categories.filter(isCupones);
  const rest = categories.filter((c) => !isCupones(c));

  // Espacio disponible para el resto (dejando lugar a Cupones al final).
  const restLimit = Math.max(0, count - cupones.length);

  const copy = [...rest];
  if (copy.length > restLimit) {
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    copy.length = restLimit;
  }

  copy.sort((a, b) => a.name.localeCompare(b.name, 'es'));
  cupones.sort((a, b) => a.name.localeCompare(b.name, 'es'));

  // Cupones SIEMPRE al final del listado.
  return [...copy, ...cupones];
}

// Una categoría abre submenú solo si tiene MÁS de un hijo; con 0 o 1 hijo se
// trata como hoja (link directo a la categoría).
export function categoryHasSubmenu(category: StoreCategory): boolean {
  return category.children.length > 1;
}

// Categorías reales de la tienda (endpoint v1, vía TanStack Query). El backend
// deduce el participante del JWT, así que no enviamos participantId.
export function useStoreCategories() {
  const { get } = useApiAuth();
  const { isLoggedIn } = useAuth();

  return useQuery({
    queryKey: ['store', 'categories'],
    queryFn: () => get<StoreCategory[]>(endpoints.store.categories),
    enabled: isLoggedIn,
  });
}
